import { React, useEffect, useState, useRef, useCallback } from 'react';
import { Row, Col } from 'react-bootstrap';
import Table from 'react-bootstrap/Table';
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import "./index.scss";

import ProfileAvatar from '../../../ui/ProfileAvatar';
import Button from "../../../ui/Button";
import Pagination from "../../../ui/Pagination";
import SideModal from '../../../ui/SideModal';
import NoDataFound from "../../../ui/NoDataFound";
import DropDown from "../../../ui/DropDown";
import RadioButton from "../../../ui/RadioButton";
import SkeletonLoader from "../../../ui/SkeletonLoader";

import ModifyMonitorModal from '../../../PostLogin/Monitor/ModifyMonitorModal';

import { formatDate, getCustomerImageUrl } from "../../../../utils/util";
import { editIcon, filterIcon, filterCloseIcon, searchIcon } from "../../../../utils/icons";

import { useSelector, useDispatch } from 'react-redux';
import { customersListForMonitors } from '../../../../store/actions/monitorAction';
import { resetAssignUnassignResponse } from "../../../../store/actions/organizationActions";

const sortByOption = [
    {
        label: "Name",
        value: "name"
    },
    {
        label: "Subscription",
        value: "subscription"
    },
    {
        label: "Expiry",
        value: "expiry"
    },
    {
        label: "Created At",
        value: "created_at"
    },
    {
        label: "Updated At",
        value: "update_at"
    }
]

const filterRadioOption = [    
    {
        label: "Ascending",
        value: "asc"
    },
    {
        label: "Descending",
        value: "desc"
    }
]

const statusOption = [
    {
        label: "Active",
        value: "active"
    },
    {
        label: "Disabled",
        value: "disabled"
    }
]

const subscriptionOption = [
    {
        label: "Demo",
        value: "trial"
    },
    {
        label: "Business",
        value: "standard"
    }
]


const MonitorTab = ({monitorData}) => {

    const customersListForMonitorsResponse = useSelector((state) => state.monitor?.customersListForMonitorsResponse)
    const assignUnAssignResponse = useSelector((state) => state.organization?.assignUnAssignResponse)

    const { t } = useTranslation();

    const filterWrapperRef = useRef(null);

    const dispatch = useDispatch();

    // customer search
    const [domainSearchValue, setDomainSearchValue] = useState("");
    const [customerSearchedValue, setCustomerSearchedValue] = useState("");
    const [isCustomerSearchFocused, setIsCustomerSearchFocused] = useState(false);
    //filter values
    const [selectedStatus, setSelectedStatus] = useState("");
    const [selectedSubscription, setSelectedSubscription] = useState("");
    const [selectedSortby, setSelectedSortby] = useState("");
    const [selectedSortOrder, setSelectedSortOrder] = useState("");  
    const [showFilter, setShowFilter] = useState(false);

    //selected filter value
    const [filterSelectedStatus, setFilterSelectedStatus] = useState("");
    const [filterSelectedSubscription, setFilterSelectedSubscription] = useState("");
    const [filterSelectedSortby, setFilterSelectedSortby] = useState("");
    const [filterSelectedSortOrder, setFilterSelectedSortOrder] = useState("");
    
    const [currentPage, setCurrentPage] = useState(1);
    const [currentPageSize, setCurrentPageSize] = useState(10);
    const [isLoading, setIsLoading] = useState(false);
    const [showAddOrgSidePanel, setShowAddOrgSidePanel] = useState(false);
    
    const removeFilter = (type) => {
        setCurrentPage(1);
        if(type === "status") {
            setSelectedStatus("")
            setFilterSelectedStatus("")
        } else if(type === "subscription") {
            setSelectedSubscription("");
            setFilterSelectedSubscription("")
        } else if (type === "sortorder") {
            setSelectedSortOrder("");
            setFilterSelectedSortOrder("")
        } else if (type === "sortby") {
            setSelectedSortby("");
            setSelectedSortOrder("");
            setFilterSelectedSortby("");
            setFilterSelectedSortOrder("");
        }
    }

    const applyFilter = () => {
        setCurrentPage(1);
        setFilterSelectedStatus(selectedStatus)
        setFilterSelectedSubscription(selectedSubscription)
        setFilterSelectedSortby(selectedSortby)
        setFilterSelectedSortOrder(selectedSortOrder)
        setShowFilter(false)        
    }

    const displaySortByName = (type) => {
        if (type === "status") {
            const matchedOption = statusOption.find((data) => data.value === selectedStatus)
            return matchedOption ? matchedOption.label : "";
        } else if (type === "subscription") {
            const matchedOption = subscriptionOption.find((data) => data.value === selectedSubscription)
            return matchedOption ? matchedOption.label : "";
        } else if (type === "sortOrder") {
            const matchedOption = filterRadioOption.find((data) => data.value === selectedSortOrder)
            return matchedOption ? matchedOption.label : "";
        } else {
            const matchedOption = sortByOption.find((data) => data.value === selectedSortby)
            return matchedOption ? matchedOption.label : "";
        }
    }

    const filterClicked = () => {
        setShowFilter(true)
    }   

    const closeAddOrgSidePanel = () => {
        setShowAddOrgSidePanel(false)
    }

    const closeModifyModal = () => {
        if(assignUnAssignResponse?.detail?.status === "success") {
            fetchCustomersList()
        }
        dispatch(resetAssignUnassignResponse())
        closeAddOrgSidePanel()
    }

    const setCurrentPageandRecordSize = (currentPage, pageSize) => {
        setCurrentPage(currentPage);
        if(pageSize !== currentPageSize) {
            setCurrentPageSize(pageSize);
            setCurrentPage(1);
        }
    }

    const handleSearchKeyDown = (event) => {
        if (event.key === "Enter" && isCustomerSearchFocused) {
            customerSearch()
        }
    }

    const customerSearch = () => {
        setCurrentPage(1)
        setCustomerSearchedValue(domainSearchValue)
    }

    const fetchCustomersList = useCallback(()=>{
        const baseUrl = `&monitor_id=${monitorData?.monitor_id}&page=${currentPage}&page_size=${currentPageSize}`;
        const queryParams = customerSearchedValue ? `&query=${customerSearchedValue}` : "";
        const sortParams = (filterSelectedStatus ? `&status=${filterSelectedStatus}` : '') + (filterSelectedSubscription ? `&subscription=${filterSelectedSubscription}` : "") + (filterSelectedSortby ? `&sort_by=${filterSelectedSortby}` : '') + (filterSelectedSortOrder ? `&sort_order=${filterSelectedSortOrder}` : '');
        const url = `${baseUrl}${queryParams}${sortParams}`;
        setIsLoading(true)
        dispatch(customersListForMonitors(url, "list"))
    },[dispatch, monitorData, currentPage, currentPageSize, customerSearchedValue, filterSelectedStatus, filterSelectedSubscription, filterSelectedSortby, filterSelectedSortOrder])

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);

        function handleClickOutside(event) {
            if (filterWrapperRef.current && !filterWrapperRef.current.contains(event.target)) {
                if (showFilter) {
                    setShowFilter(false)
                }
            }

        }
    }, [showFilter])

    useEffect(()=>{
        fetchCustomersList()
    },[fetchCustomersList])

    useEffect(()=>{
        if(customersListForMonitorsResponse !== null) {
            setIsLoading(false)
        }
    },[customersListForMonitorsResponse])


    return (
        <Row>
            <Col md={12}>
                <div className="search-filter-container my-3 d-flex justify-content-between align-items-center">
                    <div className="search-container d-flex align-items-center gap-3">
                        <div className="position-relative" onKeyDown={handleSearchKeyDown}>
                            <input className="search-input rounded" 
                                placeholder="Search Monitor" 
                                value={domainSearchValue} 
                                onChange={(e) => setDomainSearchValue(e.target.value)}
                                onFocus={() => setIsCustomerSearchFocused(true)}
                                onBlur={() => setIsCustomerSearchFocused(false)}
                            />
                            <span className="position-absolute search-icon">{searchIcon()}</span>
                        </div>
                        <button className="search-btn" onClick={customerSearch}>Search</button>
                        <div className="position-relative">
                            <span className="filter-icon" onClick={filterClicked}>{filterIcon()}</span>
                            {showFilter && <div className="filters-container position-absolute p-3 rounded" ref={filterWrapperRef}>
                                <h6>Status</h6>
                                    <DropDown 
                                        placeHolder={"Status"}
                                        options={statusOption}
                                        isMulti={false}
                                        isSearchable={false}
                                        onChange={(data)=>{setSelectedStatus(data.value)}}
                                        defaultSelectedValue={selectedStatus}
                                    />
                                <h6 className="mt-4">Subscription Type</h6>
                                    <DropDown 
                                        placeHolder={"Subscription Type"}
                                        options={subscriptionOption}
                                        isMulti={false}
                                        isSearchable={false}
                                        onChange={(data)=>{setSelectedSubscription(data.value)}}
                                        defaultSelectedValue={selectedSubscription}
                                    />
                                <h6 className="mt-4">Sort By</h6>
                                    <DropDown 
                                        placeHolder={"Sort By"}
                                        options={sortByOption}
                                        isMulti={false}
                                        isSearchable={false}
                                        onChange={(data)=>{setSelectedSortby(data.value)}}
                                        defaultSelectedValue={selectedSortby}
                                    />
                                <h6 className="mt-4">Sort Order</h6>
                                <div className="filter-user-status d-flex mb-4 gap-4">
                                    {
                                        filterRadioOption.map((data,index)=>{
                                            return (
                                                <RadioButton radioId={data.label + index} radioValue={data.value} radioLabel={data.label} onChange={(data)=>{setSelectedSortOrder(data)}} key={index} isChecked={data.value === selectedSortOrder} />
                                            )
                                        })
                                    }
                                </div>
                                <div className="filter-apply-btn mt-3">
                                    <Button buttonText={"Apply"} buttonType={"primary"} disableButton={false} buttonClicked={applyFilter} />
                                </div>
                            </div>}
                        </div>
                        {!showFilter && <p className="selected-filters">
                            {filterSelectedStatus !== "" && <span className="d-inline-block mx-2">{displaySortByName("status")} <span onClick={() => removeFilter("status")}>{filterCloseIcon()}</span></span>}
                            {filterSelectedSubscription !== "" && <span className="d-inline-block mx-2">{displaySortByName("subscription")} <span onClick={() => removeFilter("subscription")}>{filterCloseIcon()}</span></span>}
                            {filterSelectedSortby !== "" && <span className="d-inline-block mx-2">{displaySortByName("sortby")} <span onClick={() => removeFilter("sortby")}>{filterCloseIcon()}</span></span>}
                            {filterSelectedSortOrder !== "" && <span className="d-inline-block mx-2">{displaySortByName("sortOrder")} <span onClick={() => removeFilter("sortorder")}>{filterCloseIcon()}</span></span>}
                        </p>}
                    </div>
                    <div className="sort-edit-container">
                        <div className="container d-flex gap-3 align-items-center">                            
                            <button className="edit-btn" onClick={() => {
                                setShowAddOrgSidePanel(true);
                            }}>
                                <p className="d-inline-block mx-1">{editIcon()}</p>
                                {t("organizationDetailsPage.modifyBtn")}
                            </button>
                        </div>
                    </div>
                </div>
                <div className="domain-table-container monitor-page-org-table">
                    {
                        isLoading ?
                        <SkeletonLoader type={"line"} count={5} currentPage={"customersList"} />
                        :
                        <Table responsive="lg">
                            <thead>
                                <tr>
                                    <th>Customer Details</th>
                                    <th>Subscription Type</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    customersListForMonitorsResponse?.results?.length > 0 &&
                                    customersListForMonitorsResponse?.results?.map((data, index) => {
                                        return (
                                            <motion.tr
                                                key={index}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.4, delay: index * 0.1 }}
                                            >
                                                <td>
                                                    <ProfileAvatar type="profile" firstName={data?.name} lastName={""} secondaryText={formatDate(data?.created_at)} imageUrl={getCustomerImageUrl(data)} />
                                                </td>
                                                <td>
                                                    {data?.subscription.name === "trial" ? "Demo" : "Business"}
                                                </td>
                                                <td>
                                                    {data?.status === "active" && <p className="business-impact critical">{data?.status}</p>}
                                                    {data?.status === "disabled" && <p className="business-impact high">{data?.status}</p>}
                                                </td>
                                            </motion.tr>
                                        )
                                    })
                                }                          
                            </tbody>
                        </Table>
                    }
                    
                    {
                        !isLoading && customersListForMonitorsResponse?.results?.length === 0 &&
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4 }}
                        >
                            <NoDataFound />
                        </motion.div>
                        
                    }
                    {customersListForMonitorsResponse !== null && !isLoading && customersListForMonitorsResponse?.meta?.total_results !== 0 && <Pagination
                        className="pagination-bar"
                        currentPage={currentPage}
                        pageSize={currentPageSize}
                        totalCount={customersListForMonitorsResponse?.meta?.total_results}
                        onPageChange={setCurrentPageandRecordSize}
                    />}
                </div>
            </Col>
            <SideModal showSidePanel={showAddOrgSidePanel} hideSidePanel={closeModifyModal}>
                {showAddOrgSidePanel && <ModifyMonitorModal monitorDetails={monitorData} clsoeModifyModal={closeModifyModal}/>}
            </SideModal>
        </Row>
    )
}

export default MonitorTab;